/// <reference types="monaco-editor" />
import React from 'react';
import * as PropTypes from 'prop-types';
import { prepareMonaco } from './loader';

const DEFAULT_EDITOR_CONFIG = {
	// theme: 'vs-dark',
	scrollBeyondLastLine: false,
	hideCursorInOverviewRuler: true,
	scrollbar: {
		verticalScrollbarSize: 12,
		horizontalScrollbarSize: 12,
	}
};

const VALUE_PREFERENCE = {
	preserveBOM: false,
	lineEnding: '\n',
};

export class MonacoEditor extends React.Component {

	static propTypes = {
		defaultValue: PropTypes.string,
		language: PropTypes.string,
		autoFocus: PropTypes.bool,
		enableWarning: PropTypes.bool,
	};

	state = {
		ready: false,
		focused: true,
	};

	createPropsCallback = method => (...args) => {
		if (typeof this.props[method] === 'function') {
			return this.props[method](...args);
		}
	};
	callOnChange = this.createPropsCallback('onChange');
	callOnSave = this.createPropsCallback('onSave');
	callOnBlur = this.createPropsCallback('onBlur');
	callOnFocus = this.createPropsCallback('onFocus');

	render() {
		const { placeholder } = this.props;
		const { ready, focused, empty } = this.state;
		return (
			<div
				className="monaco-editor-wrap"
				ref={$editor => this.$editor = $editor}>
				{!ready &&
					<span style={{ opacity: 0.5 }}>{this.props.loadingPlaceholder}</span>
				}
				{ready && placeholder && empty &&
					<div style={{
						position: 'absolute',
						top: 0,
						left: 0,
						zIndex: 1,
						opacity: 0.5,
						fontSize: '16px',
						pointerEvents: 'none'
					}}>{placeholder}</div>
				}
			</div>
		);
	}

	isEmpty() {
		if (!this.editor) {
			return true;
		}
		const model = this.editor.getModel();
		if (model.getLineCount() > 1) return false;
		const firstLine = model.getLineContent(1);
		return !firstLine.replace(/\u200b/g, '');
	}

	async componentDidMount() {
		const monaco = await prepareMonaco();

		// Dom will set to null after component unmount
		if (!this.$editor) return;

		const { defaultValue, language, autoFocus, enableWarning } = this.props;
		const editor = this.editor = monaco.editor.create(this.$editor, {
			...DEFAULT_EDITOR_CONFIG,
			lineNumbers: 'off',
			language,
			value: this.initialValue || defaultValue || '',
			glyphMargin: false,
			wordWrap: 'on',
			lineDecorationsWidth: 0,
			fontSize: 16,
			roundedSelection: false,
			renderLineHighlight: false,
			occurrencesHighlight: false,

			// 禁用提示
			quickSuggestions: false,
			suggestOnTriggerCharacters: false,
			wordBasedSuggestions: false,
			snippetSuggestions: false,

			fontFamily: "'pingfang SC','helvetica neue',arial,'hiragino sans gb','microsoft yahei ui','microsoft yahei',simsun,sans-serif",
		});

		if (autoFocus) {
			editor.focus();
		}

		editor.onDidChangeModelContent(this.notifyChange);
		editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_S, this.callOnSave);
		editor.onDidFocusEditor(() => this.handleFocus());
		editor.onDidBlurEditor(() => this.handleBlur());

		this.setState({
			ready: true,
			empty: this.isEmpty(),
		});

		if (typeof this.props.onReady === 'function') {
			this.props.onReady(editor);
		}
	}

	componentWillUnmount() {
		if (this.editor) {
			this.editor.getModel().dispose();
			this.editor.dispose();
		}
	}

	componentWillReceiveProps(nextProps) {
		if (this.editor && nextProps.value !== this.value) {
			this.editor.setValue(nextProps.value);
		}
	}

	focus() {
		if (this.editor) {
			this.editor.focus();
		}
	}

	handleFocus() {
		this.callOnFocus();
		this.setState({ focused: true });
	}

	handleBlur() {
		this.callOnBlur();
		this.setState({ focused: false });
	}

	notifyChange = () => {
		this.callOnChange();
		this.setState({
			empty: this.isEmpty(),
		});
	}

	getValue() {
		if (this.editor) {
			return this.editor.getValue(VALUE_PREFERENCE).replace(/\u200b/g, '');
		}
		return this.initialValue || this.props.defaultValue || '';
	}

	setValue(value) {
		if (typeof value !== 'string') {
			throw new Error('Expect setValue() to pass as string, but got `' + typeof value + '`')
		};
		if (this.editor) {
			this.editor.setValue(value);
		} else {
			// if editor is not ready, set to state. The editor will use the state value when initializing.
			this.initialValue = value;
		}
	}

	layout() {
		if (this.editor) {
			this.editor.layout();
		}
	}

	async revealLineAtTop(lineNumber) {
		const monaco = await prepareMonaco();
		const range = new monaco.Range(lineNumber, 1, lineNumber, 1);
		this.editor.revealRangeAtTop(range);
	}

	async insertTextOnNextLine(text) {
		if (!this.editor) return;

		const monaco = await prepareMonaco();
		const editor = this.editor;
		await editor.getAction('editor.action.insertLineAfter').run();
		const { lineNumber } = editor.getPosition();

		const range = new monaco.Range(lineNumber, 1, lineNumber, 1);
		const id = { major: 1, minor: 1 };
		const op = { identifier: id, range, text, forceMoveMarkers: true };

		return editor.executeEdits("insert-editor-1", [op]);
	}

	async replaceText(curText) {
		const monaco = await prepareMonaco();
		const editor = this.editor;

		editor.executeEdits('replace', [{
			identifier: 'insert',
			range: editor.getSelection(),
			text: curText,
			forceMoveMarkers: true,
		}]);
	}

	async findAndReplaceText(preText, curText) {
		const monaco = await prepareMonaco();
		const editor = this.editor;
		const lineIndex = this.value.split(/\r?\n/g).findIndex(line => line.includes(preText));
		if (lineIndex != -1) {
			editor.executeEdits('replace', [{
				identifier: 'insert',
				range: new monaco.Range(lineIndex + 1, 1, lineIndex + 1, 1000),
				text: curText,
				forceMoveMarkers: true,
			}]);

			editor.focus();
		}
	}

	async setWarnings(warnings) {
		if (!this.props.enableWarning || !this.editor || !warnings) {
			return;
		}

		const monaco = await prepareMonaco();
		const lastDecorations = this._lastEditorDecorations || [];
		this._lastEditorDecorations = this.editor.deltaDecorations(
			lastDecorations,
			warnings.map(({ line, message }) => ({
				range: new monaco.Range(line, 1, line, 10000),
				options: {
					isWholeLine: true,
					className: 'lab-editor-warning-line',
					hoverMessage: message,
					glyphMarginClassName: 'lab-editor-warning-line-glyph',
					glyphMarginHoverMessage: message,
					overviewRuler: true,
				},
			})),
		);
	}
}



// WEBPACK FOOTER //
// ./lib/editor/MarkdownEditor/MonacoEditor.jsx